/**
 * Security utility functions
 */

/**
 * Character sets for password generation
 */
const CHARACTER_SETS = {
    lowercase: 'abcdefghijklmnopqrstuvwxyz',
    uppercase: 'ABCDEFGHIJKLMNOPQRSTUVWXYZ',
    numbers: '0123456789',
    symbols: '!@#$%^&*()_+-=[]{}|;:,.<>?',
    specialSymbols: '~`!@#$%^&*()_+-={}[]|\\:";\'<>?,./',
    safe: '!@#$%^&*-_=+[]{}|:,.<>?',
};

/**
 * Default password generation options
 */
const DEFAULT_OPTIONS = {
    length: 12,
    includeLowercase: true,
    includeUppercase: true,
    includeNumbers: true,
    includeSymbols: true,
    excludeSimilar: false, // Exclude similar looking characters (0, O, l, I, 1)
    excludeAmbiguous: false, // Exclude ambiguous characters ({}, [], (), etc.)
    minNumbers: 1,
    minSymbols: 1,
    minUppercase: 1,
    minLowercase: 1,
    customCharacters: '',
    excludeCharacters: '',
};

/**
 * Similar looking characters to potentially exclude
 */
const SIMILAR_CHARACTERS = '0O1lI|';

/**
 * Ambiguous characters to potentially exclude
 */
const AMBIGUOUS_CHARACTERS = '{}[]()/\\\'"`~,;.<>';

/**
 * Generate a random password based on specified criteria
 * @param {Object} options - Password generation options
 * @param {number} options.length - Length of the password (default: 12)
 * @param {boolean} options.includeLowercase - Include lowercase letters (default: true)
 * @param {boolean} options.includeUppercase - Include uppercase letters (default: true)
 * @param {boolean} options.includeNumbers - Include numbers (default: true)
 * @param {boolean} options.includeSymbols - Include symbols (default: true)
 * @param {boolean} options.excludeSimilar - Exclude similar looking characters (default: false)
 * @param {boolean} options.excludeAmbiguous - Exclude ambiguous characters (default: false)
 * @param {number} options.minNumbers - Minimum number of numeric characters (default: 1)
 * @param {number} options.minSymbols - Minimum number of symbol characters (default: 1)
 * @param {number} options.minUppercase - Minimum number of uppercase characters (default: 1)
 * @param {number} options.minLowercase - Minimum number of lowercase characters (default: 1)
 * @param {string} options.customCharacters - Additional characters to include
 * @param {string} options.excludeCharacters - Characters to exclude
 * @returns {string} Generated password
 */
export function generatePassword(options = {}) {
    const config = { ...DEFAULT_OPTIONS, ...options };

    // Validate length
    if (config.length < 1) {
        throw new Error('Password length must be at least 1');
    }

    // Build a character pool
    let characterPool = '';
    let requiredChars = [];

    if (config.includeLowercase) {
        let chars = CHARACTER_SETS.lowercase;
        if (config.excludeSimilar) {
            chars = chars.replace(/[lI]/g, '');
        }
        characterPool += chars;

        // Ensure minimum lowercase characters
        if (config.minLowercase > 0) {
            for (let i = 0; i < config.minLowercase; i++) {
                requiredChars.push(getRandomChar(chars));
            }
        }
    }

    if (config.includeUppercase) {
        let chars = CHARACTER_SETS.uppercase;
        if (config.excludeSimilar) {
            chars = chars.replace(/[OI]/g, '');
        }
        characterPool += chars;

        // Ensure minimum uppercase characters
        if (config.minUppercase > 0) {
            for (let i = 0; i < config.minUppercase; i++) {
                requiredChars.push(getRandomChar(chars));
            }
        }
    }

    if (config.includeNumbers) {
        let chars = CHARACTER_SETS.numbers;
        if (config.excludeSimilar) {
            chars = chars.replace(/[01]/g, '');
        }
        characterPool += chars;

        // Ensure minimum numeric characters
        if (config.minNumbers > 0) {
            for (let i = 0; i < config.minNumbers; i++) {
                requiredChars.push(getRandomChar(chars));
            }
        }
    }

    if (config.includeSymbols) {
        let chars = CHARACTER_SETS.safe;
        if (config.excludeAmbiguous) {
            chars = chars.replace(/[{}[\]()/\\'"`,;.<>]/g, '');
        }
        if (config.excludeSimilar) {
            chars = chars.replace(/[|]/g, '');
        }
        characterPool += chars;

        // Ensure minimum symbol characters
        if (config.minSymbols > 0) {
            for (let i = 0; i < config.minSymbols; i++) {
                requiredChars.push(getRandomChar(chars));
            }
        }
    }

    // Add custom characters
    if (config.customCharacters) {
        characterPool += config.customCharacters;
    }

    // Remove excluded characters
    if (config.excludeCharacters) {
        for (const char of config.excludeCharacters) {
            characterPool = characterPool.replace(new RegExp(escapeRegExp(char), 'g'), '');
        }
    }

    // Remove similar characters if requested
    if (config.excludeSimilar) {
        for (const char of SIMILAR_CHARACTERS) {
            characterPool = characterPool.replace(new RegExp(escapeRegExp(char), 'g'), '');
        }
    }

    // Remove ambiguous characters if requested
    if (config.excludeAmbiguous) {
        for (const char of AMBIGUOUS_CHARACTERS) {
            characterPool = characterPool.replace(new RegExp(escapeRegExp(char), 'g'), '');
        }
    }

    if (characterPool.length === 0) {
        throw new Error('No valid characters available for password generation');
    }

    // Check if required characters exceed the password length
    if (requiredChars.length > config.length) {
        throw new Error('Minimum character requirements exceed password length');
    }

    // Generate the password
    let password = [...requiredChars];

    // Fill the remaining length with random characters
    while (password.length < config.length) {
        password.push(getRandomChar(characterPool));
    }

    // Shuffle the password to avoid predictable patterns
    return shuffleArray(password).join('');
}

/**
 * Generate multiple passwords at once
 * @param {number} count - Number of passwords to generate
 * @param {Object} options - Password generation options
 * @returns {string[]} Array of generated passwords
 */
export function generateMultiplePasswords(count = 1, options = {}) {
    if (count < 1) {
        throw new Error('Count must be at least 1');
    }

    const passwords = [];
    for (let i = 0; i < count; i++) {
        passwords.push(generatePassword(options));
    }

    return passwords;
}

/**
 * Check password strength
 * @param {string} password - Password to check
 * @returns {Object} Strength analysis
 */
export function checkPasswordStrength(password) {
    if (!password) {
        return { score: 0, level: 'Very Weak', feedback: ['Password is empty'] };
    }

    const feedback = [];
    let score = 0;

    // Length check
    if (password.length >= 8) score += 1;
    else feedback.push('Use at least 8 characters');

    if (password.length >= 12) score += 1;
    if (password.length >= 16) score += 1;

    // Character variety checks
    if (/[a-z]/.test(password)) score += 1;
    else feedback.push('Include lowercase letters');

    if (/[A-Z]/.test(password)) score += 1;
    else feedback.push('Include uppercase letters');

    if (/[0-9]/.test(password)) score += 1;
    else feedback.push('Include numbers');

    if (/[^a-zA-Z0-9]/.test(password)) score += 1;
    else feedback.push('Include symbols');

    // Bonus points for good practices
    if (password.length >= 20) score += 1;
    if (/[!@#$%^&*]/.test(password)) score += 1;

    // Penalties for bad practices
    if (/(.)\1{2,}/.test(password)) {
        score -= 1;
        feedback.push('Avoid repeating characters');
    }

    if (/123|abc|qwe|password|admin/i.test(password)) {
        score -= 2;
        feedback.push('Avoid common patterns and words');
    }

    // Determine level
    let level;
    if (score >= 7) level = 'Very Strong';
    else if (score >= 5) level = 'Strong';
    else if (score >= 3) level = 'Medium';
    else if (score >= 1) level = 'Weak';
    else level = 'Very Weak';

    return {
        score: Math.max(0, score),
        level,
        feedback: feedback.length > 0 ? feedback : ['Good password!'],
        length: password.length,
        hasLowercase: /[a-z]/.test(password),
        hasUppercase: /[A-Z]/.test(password),
        hasNumbers: /[0-9]/.test(password),
        hasSymbols: /[^a-zA-Z0-9]/.test(password),
    };
}

/**
 * Generate a memorable password using word patterns
 * @param {Object} options - Generation options
 * @returns {string} Memorable password
 */
export function generateMemorablePassword(options = {}) {
    const config = {
        wordCount: 4,
        separator: '-',
        includeNumbers: true,
        capitalizeWords: true,
        ...options,
    };

    // Simple word list for memorable passwords
    const words = [
        'apple', 'brave', 'chair', 'dance', 'eagle', 'flame', 'grape', 'house',
        'ice', 'jump', 'kite', 'light', 'moon', 'night', 'ocean', 'peace',
        'quiet', 'river', 'stone', 'tiger', 'under', 'voice', 'water', 'yellow',
    ];

    let password = [];

    for (let i = 0; i < config.wordCount; i++) {
        let word = words[Math.floor(Math.random() * words.length)];
        if (config.capitalizeWords) {
            word = word.charAt(0).toUpperCase() + word.slice(1);
        }
        password.push(word);
    }

    if (config.includeNumbers) {
        password.push(Math.floor(Math.random() * 100).toString().padStart(2, '0'));
    }

    return password.join(config.separator);
}

/**
 * Helper function to get a random character from a string
 * @param {string} chars - Characters to choose from
 * @returns {string} Random character
 */
function getRandomChar(chars) {
    return chars.charAt(Math.floor(Math.random() * chars.length));
}

/**
 * Helper function to shuffle an array
 * @param {Array} array - Array to shuffle
 * @returns {Array} Shuffled array
 */
function shuffleArray(array) {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
}

/**
 * Helper function to escape special regex characters
 * @param {string} string - String to escape
 * @returns {string} Escaped string
 */
function escapeRegExp(string) {
    return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

// Export character sets for external use if needed
export { CHARACTER_SETS };